import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Transformer V₁/V₂ = N₁/N₂ — laminated core, primary + secondary windings, voltmeters. */
export function DiagramPrimarySecondary() {
  const cx = 175;
  // Core rectangle
  const ct = 60, cb = 200;
  const cl = cx - 22, cr = cx + 22;

  return (
    <DiagramCanvas>
      {/* Laminated iron core (3-leg style) */}
      <Rect x={cl - 4} y={ct} width={(cr - cl) + 8} height={cb - ct} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.6" />
      {/* Lamination lines */}
      {Array.from({ length: 6 }).map((_, i) => (
        <Line key={i} x1={cl - 3 + i} y1={ct + 2} x2={cl - 3 + i} y2={cb - 2}
          stroke={DT.stroke} strokeWidth="0.4" opacity={0.4} />
      ))}
      {/* Top + bottom yoke labels */}
      <SvgText x={cx} y={cb + 14} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle">iron core</SvgText>

      {/* Primary winding (left coil — many tight loops) */}
      {Array.from({ length: 10 }).map((_, i) => {
        const yp = 80 + i * 12;
        return (
          <Path key={i} d={`M${cl - 18},${yp} A8,5 0 0 1 ${cl - 18},${yp + 12}`}
            fill="none" stroke={DT.stroke} strokeWidth="1.4" />
        );
      })}
      <Line x1={cl - 18} y1={80} x2={cl - 18} y2={200} stroke={DT.stroke} strokeWidth="0.5" opacity={0.4} />

      {/* Secondary winding (right coil — fewer loops = step-down example) */}
      {Array.from({ length: 5 }).map((_, i) => {
        const yp = 90 + i * 22;
        return (
          <Path key={i} d={`M${cr + 18},${yp} A8,8 0 0 0 ${cr + 18},${yp + 22}`}
            fill="none" stroke={DT.stroke} strokeWidth="1.6" />
        );
      })}
      <Line x1={cr + 18} y1={90} x2={cr + 18} y2={200} stroke={DT.stroke} strokeWidth="0.5" opacity={0.4} />

      {/* Primary leads */}
      <Line x1={cl - 18} y1={80} x2={40} y2={80} stroke={DT.stroke} strokeWidth="1.6" />
      <Line x1={cl - 18} y1={200} x2={40} y2={200} stroke={DT.stroke} strokeWidth="1.6" />
      {/* V1 voltmeter */}
      <Circle cx={32} cy={140} r={12} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.4" />
      <SvgText x={32} y={144} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">V₁</SvgText>
      <Line x1={40} y1={80} x2={40} y2={128} stroke={DT.leader} strokeWidth="0.8" strokeDasharray="2,2" />
      <Line x1={40} y1={152} x2={40} y2={200} stroke={DT.leader} strokeWidth="0.8" strokeDasharray="2,2" />

      {/* Secondary leads */}
      <Line x1={cr + 18} y1={90} x2={310} y2={90} stroke={DT.stroke} strokeWidth="1.6" />
      <Line x1={cr + 18} y1={200} x2={310} y2={200} stroke={DT.stroke} strokeWidth="1.6" />
      <Circle cx={320} cy={145} r={12} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.4" />
      <SvgText x={320} y={149} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">V₂</SvgText>

      {/* Magnetic flux arrow inside core */}
      <Polygon points={`${cx},${100} ${cx - 4},${108} ${cx + 4},${108}`} fill={DT.varColor} opacity={0.6} />
      <Polygon points={`${cx},${160} ${cx - 4},${168} ${cx + 4},${168}`} fill={DT.varColor} opacity={0.6} />
      <SvgText x={cx + 8} y={134} fill={DT.varColor} fontSize="8" fontFamily={DT.mono}>Φ</SvgText>

      {/* Turn count badges */}
      <Rect x={cl - 36} y={62} width={28} height={12} fill="rgba(0,0,0,0.45)" />
      <SvgText x={cl - 22} y={71} fill={DT.varColor} fontSize="8" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">N₁</SvgText>
      <Rect x={cr + 8} y={62} width={28} height={12} fill="rgba(0,0,0,0.45)" />
      <SvgText x={cr + 22} y={71} fill={DT.varColor} fontSize="8" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">N₂</SvgText>

      {/* Labels */}
      <LeaderLine x1={cl - 18} y1={140} x2={20} y2={50} varName="primary" desc="N₁ turns" anchor="start" />
      <LeaderLine x1={cr + 18} y1={140} x2={310} y2={50} varName="secondary" desc="N₂ turns" anchor="end" />

      <FormulaTag formula="V₁/V₂ = N₁/N₂ = I₂/I₁    (ideal transformer)" />
    </DiagramCanvas>
  );
}
